import {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';

import {
  ConfigProvider,
  Drawer,
  InputNumber,
  InputNumberProps,
} from 'antd';
import {
  formatUnits,
  parseUnits,
} from 'ethers';
import { useTranslation } from 'react-i18next';

import { tokenArr } from '@/collocate';
import useBestTradeExactIn from '@/hooks/useBestTradeExactIn';
import useControllablePolling from '@/hooks/usePollingEffect';
import arrows from '@/image/arrows.svg';
import bankCard from '@/image/bankCard.svg';
import useConnectWallet from '@/store/wallet/useConnectWallet';
import {
  formatTo6Decimals,
  mobileHidden,
  multiplyDecimal,
} from '@/utils';
import {
  CloseOutlined,
  LoadingOutlined,
} from '@ant-design/icons';

import BalanceCom from './BalanceCom';
import Copy from './Copy';
import Icon from './Icon';
import PresentData from './PresentData';
import ScrollView from './ScrollView';

// 定义组件 props 类型
type InputBoxType = {
    tokenAddressIndex: number, // 当前选中代币索引
    amountTo: string,          // 输入框的值
    inputLod: boolean,         // 输入框 loading 状态
    getSelectedTokenIndex: (index: number) => void, // 通知父组件选中新的代币索引
    setAmountFrom: (amount: string) => void,        // 更新输入金额
    tokenBalance?: bigint,        // 当前代币余额
    tokenBalanceLod?: boolean,    // 余额加载状态
    type: 0 | 1                    // 0 swap 1 addLiquidity
    onFocusF?: Function
}

// 目标代币输入框组件
const InputBoxTo = forwardRef(({ tokenAddressIndex, onFocusF, amountTo, getSelectedTokenIndex, setAmountFrom, inputLod, type, tokenBalance, tokenBalanceLod }: InputBoxType, ref) => {
    const { address } = useConnectWallet(); // 获取连接的钱包地址
    const { t } = useTranslation()
    // 控制代币选择抽屉的开关
    const [open, setOpen] = useState(false);
    // 当前选中代币的信息
    const tokenInfo = useMemo(() => {
        return tokenArr[tokenAddressIndex]
    }, [tokenAddressIndex]);
    const {
        bestTrade, // 最优路径及输出金额
        bestTradeExactInLod, // 是否加载中
        getBestTradePath,
    } = useBestTradeExactIn(tokenInfo?.address, tokenArr[1].address, parseUnits('1')); // 获取兑换比例 hook
    const { path, amountOut } = bestTrade

    const isUSDRAddress = useMemo(() => tokenInfo?.address === tokenArr[1].address, [tokenInfo]); // 判断是否为 USDR 代币
    // 输入框变化事件，限制为最多 18 位小数
    const onChange: InputNumberProps['onChange'] = (value) => {
        const strValue = typeof value === 'string' ? value : String(value ?? '');
        if (strValue === '0') return setAmountFrom('');
        setAmountFrom(formatTo6Decimals(strValue, 18));
    };
    // 定义子组件暴露出去的方法
    useImperativeHandle(ref, () => ({
        getAmountsOutCall: () => {
            getBestTradePath()
        },
    }));

    // 打开代币选择抽屉
    const showDrawer = () => {
        setOpen(true);
    };
    // 关闭代币选择抽屉
    const onClose = () => {
        setOpen(false);
    };
    // 选择代币事件
    const getTokenAddressIndex = (index: number) => {
        if (index === tokenAddressIndex) return onClose();
        getSelectedTokenIndex(index);
        onClose();
    }
    // 聚焦输入框
    const onFocus = () => {
        onFocusF && onFocusF()
    }
    // 计算兑输出的代币价值多少USDR（amountsOut1 =  当前代币价值 *用户输入数量 ）
    const amountsOut1 = useMemo(() => {
        if (isUSDRAddress) return multiplyDecimal(1, amountTo || 0);
        const num = amountOut ? multiplyDecimal(formatUnits(amountOut), amountTo || 0) : 0;
        return num;
    }, [amountTo, amountOut, isUSDRAddress]);
    useControllablePolling(() => {
        getBestTradePath()
    }, 10000, (!bestTradeExactInLod && path.length), false)
    return (
        <ConfigProvider
            theme={{
                components: {
                    InputNumber: {
                        inputFontSizeLG: 26,
                        paddingBlockLG: 6,
                        paddingInlineLG: 0
                    }
                }
            }} >
            {/* 主输入框区域 */}
            <div className="bg-white border border-ee rounded-5 p-2.5 ">
                {/* 顶部标签栏 */}
                <div className="flex items-center justify-between">
                    <div className="text-a8 text-sm">{type ? t('com.input') : t('com.buy')}</div>
                    {address && !!type && (
                        <div className="text-a8 text-xs flex items-center">
                            <Icon src={bankCard} className="w-3 mr-1" />
                            <BalanceCom balance={tokenBalance || BigInt(0)} loading={tokenBalanceLod || false} />
                            {/* MAX 按钮：点击时自动填入最大可用余额 */}
                            <span
                                onClick={() => tokenBalance && onChange(formatUnits(tokenBalance))}
                                className="pl-1.25 cursor-pointer underline text-00b decoration-00b"
                            >
                                MAX
                            </span>
                        </div>
                    )}
                </div>
                {/* 输入框 + 代币选择按钮 */}
                <div className='flex items-center justify-between mt-1.75'>
                    <InputNumber
                        onFocus={onFocus}
                        value={(amountTo) || ''}
                        prefix={inputLod ? <LoadingOutlined /> : null}
                        controls={false}
                        stringMode
                        onChange={onChange}
                        className='w-full font-medium mr-2'
                        size='large'
                    />
                    {/* 代币选择按钮 */}
                    {!tokenInfo ?
                        <div onClick={showDrawer} className='cursor-pointer box-border pl-3.75 pr-3 py-2 flex items-center border rounded-5 border-e2'>
                            <span className='text-1d text-sm mr-2.25 whitespace-nowrap'>{t('com.selectToken')}</span>
                            <Icon src={arrows} className='w-3' />
                        </div> :
                        <div onClick={showDrawer} className='cursor-pointer box-border px-2.5 py-1.5 flex items-center border rounded-5 border-e2'>
                            <div className='w-6 h-6 rounded-full overflow-hidden mr-1.5'>
                                <img src={tokenInfo.logo} alt="" />
                            </div>
                            <span className='text-1d text-sm mr-2.5'>{tokenInfo.symbol}</span>
                            <Icon src={arrows} className='w-3' />
                        </div>
                    }
                </div>
                {/* 显示兑换后的金额 */}
                <PresentData
                    className="text-a8 text-xs mt-0.5"
                    qText="$"
                    value={(amountsOut1)}
                    loading={bestTradeExactInLod}
                />
            </div>
            {/* 代币选择抽屉 */}
            <Drawer
                className="rounded-t-3xl md:rounded-3xl  md:w-95 md:h-[70%] md:absolute  md:top-1/2 md:left-1/2  md:-translate-y-[80%] md:-translate-x-1/2"
                title=""
                closeIcon={null}
                onClose={onClose}
                open={open}
                placement='bottom'
                height='80%'
            >
                {/* 抽屉顶部标题与关闭按钮 */}
                <div className='flex items-center justify-between'>
                    {!tokenInfo ?
                        <div className='font-medium'>{t('com.selectToken')}</div>
                        : <div className='flex items-center'>
                            <div className='w-6 h-6 rounded-full overflow-hidden mr-1.5'>
                                <img src={tokenInfo.logo} alt="" />
                            </div>
                            <span className='text-1d font-semibold text-base mr-1'>{tokenInfo.symbol}</span>
                            <Copy text={tokenInfo.address} className='w-4' />
                        </div>
                    }
                    <CloseOutlined onClick={onClose} className='text-a8 text-base' />
                </div>
                {/* 代币列表 */}
                <ScrollView px='0px' className="mt-5 h-[90%] overflow-y-auto md:h-crawerH">
                    {tokenArr.map((item, index) => {
                        return <div onClick={() => { getTokenAddressIndex(index) }} className='cursor-pointer flex items-center justify-between mb-2 bg-gray-100 p-2.5 rounded-xl' key={index}>
                            <div className='flex items-center'>
                                <div className='w-10 h-10 rounded-full overflow-hidden mr-1.5'>
                                    <img src={item.logo} className='' alt="" />
                                </div>
                                <div>
                                    <div className='text-mainBlack text-sm font-semibold'>{item.symbol}</div>
                                    <div className='text-a8 text-xs flex items-center'>
                                        {mobileHidden(item.address)}
                                        <Copy text={item.address} className='w-3 ml-1' />
                                    </div>
                                </div>
                            </div>
                            <Icon src={arrows} className='w-4 text-00b  -rotate-90' />
                        </div>
                    })}
                </ScrollView>
            </Drawer>
        </ConfigProvider>
    )
})
export default InputBoxTo